import getSubproductDetailsTotalExpense from 'services/products/calculations/getSubproductDetailsTotalExpense';
import getSubproductDetailsTotalSellingPrice from 'services/products/calculations/getSubproductDetailsTotalSellingPrice';
import {
  SUBCONTRACTING,
  sectionOf,
} from 'services/offers/installationSections';

import getPriceSummary from '../../getPriceSummary';
import IInstallation from '../../IInstallation';
import ISubproduct from '../../ISubproduct';

export interface ISupplierSummaryRow {
  contributionMargin: number;
  contributionMarginRatio: number;
  expense: number;
  // The rows of other expenses of a category: its name in supplier, without a supplier
  otherExpense?: boolean;
  // null for the rows whose supplier is not known
  supplier: null | string;
  sellingPrice: number;
}

/*
 * The internal summary of the calculation rows by supplier:
 * - a material row: the supplier of its product; a material priced from its sub rows
 *   is shared by the suppliers of the products of its sub rows
 * - own installation work and own production: the department of the offer (ownWork)
 * - other expenses: by their category (the rows do not know their supplier), after the
 *   suppliers
 * - subcontracting and other expenses without a category: not known yet
 * The rows whose supplier is not known are summed on one row, which comes last.
 */
const getSupplierSummary = ({
  installations,
  isOtherExpense,
  otherExpenseCategory = () => null,
  ownWork,
  quantity,
  subproducts,
  supplierOfProduct,
}: {
  installations: IInstallation[];
  isOtherExpense: (installation: IInstallation) => boolean;
  // The name of the category of an other expense, null without one
  otherExpenseCategory?: (installation: IInstallation) => null | string;
  // The supplier of the own work: the department of the offer
  ownWork: string;
  quantity: number;
  subproducts: ISubproduct[];
  supplierOfProduct: (productId: number) => null | string;
}): ISupplierSummaryRow[] => {
  const groups = new Map<
    null | string,
    {
      // The parts of the materials priced from their sub rows, by the sub rows
      detailExpense: number;
      detailSellingPrice: number;
      installations: IInstallation[];
      otherExpense: boolean;
      subproducts: ISubproduct[];
      supplier: null | string;
    }
  >();
  // A category of other expenses apart from a supplier of the same name
  const groupOf = (supplier: null | string, otherExpense = false) => {
    const key = otherExpense ? `other-expense:${supplier}` : supplier;
    const existing = groups.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const group = {
      detailExpense: 0,
      detailSellingPrice: 0,
      installations: [] as IInstallation[],
      otherExpense,
      subproducts: [] as ISubproduct[],
      supplier,
    };
    groups.set(key, group);
    return group;
  };

  const supplierOf = (product: ISubproduct['product']) => {
    const productId = Number(product?.value);
    return productId ? supplierOfProduct(productId) : null;
  };

  subproducts.forEach((subproduct) => {
    // A material priced from its sub rows (often with a free text name): its totals are
    // shared by the suppliers of the sub rows, in the shares of their prices
    if (subproduct.calculatePriceFromDetails && subproduct.details.length > 0) {
      const row = getPriceSummary([], [subproduct], quantity);
      const parts = subproduct.details.map((detail) => ({
        expense: getSubproductDetailsTotalExpense([detail]),
        sellingPrice: getSubproductDetailsTotalSellingPrice([detail]),
        supplier: supplierOf(detail.product),
      }));
      const expenseSum = parts.reduce((sum, part) => sum + part.expense, 0);
      const sellingPriceSum = parts.reduce(
        (sum, part) => sum + part.sellingPrice,
        0
      );
      const share = (part: number, sum: number) =>
        sum > 0 ? part / sum : 1 / parts.length;

      parts.forEach((part) => {
        const group = groupOf(part.supplier);
        group.detailExpense += row.expenseSum * share(part.expense, expenseSum);
        group.detailSellingPrice +=
          row.sellingPrice * share(part.sellingPrice, sellingPriceSum);
      });
      return;
    }

    const supplier = subproduct.isCustomSubproduct
      ? null
      : supplierOf(subproduct.product);
    groupOf(supplier).subproducts.push(subproduct);
  });

  installations.forEach((installation) => {
    if (isOtherExpense(installation)) {
      const category = otherExpenseCategory(installation);
      groupOf(category, category !== null).installations.push(installation);
      return;
    }

    const isOwnWork = sectionOf(installation) !== SUBCONTRACTING;
    groupOf(isOwnWork ? ownWork : null).installations.push(installation);
  });

  const rows = Array.from(groups.values()).map((group) => {
    const { otherExpense, supplier } = group;
    const summary = getPriceSummary(
      group.installations,
      group.subproducts,
      quantity
    );
    if (group.detailExpense === 0 && group.detailSellingPrice === 0) {
      return {
        contributionMargin: summary.shownContributionMargin,
        contributionMarginRatio: summary.contributionMarginRatio,
        expense: summary.expenseSum,
        otherExpense,
        sellingPrice: summary.sellingPrice,
        supplier,
      };
    }

    // With the parts of the sub rows
    const expense = summary.expenseSum + group.detailExpense;
    const sellingPrice = summary.sellingPrice + group.detailSellingPrice;
    const contributionMargin =
      summary.shownContributionMargin +
      group.detailSellingPrice -
      group.detailExpense;
    return {
      contributionMargin,
      contributionMarginRatio:
        sellingPrice === 0 ? 0 : (contributionMargin / sellingPrice) * 100,
      expense,
      otherExpense,
      sellingPrice,
      supplier,
    };
  });

  // The suppliers, then the categories of other expenses, the unknown row last
  const order = (row: ISupplierSummaryRow) => {
    if (row.supplier === null) {
      return 2;
    }
    return row.otherExpense ? 1 : 0;
  };
  return rows.sort(
    (a, b) =>
      order(a) - order(b) ||
      (a.supplier ?? '').localeCompare(b.supplier ?? '', 'fi')
  );
};

export default getSupplierSummary;
